/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import styles from '../../../style';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import {
  CheckOutlined,
  ClearOutlined,
  Edit,
  DeleteOutline,
} from '@material-ui/icons';
import { connect } from 'react-redux';
import {
  saveAllergy,
  updateAllergy,
  deleteAllergy,
  getPatientMedicalHistory,
} from 'services/soap/action';

// ---------------------------------------------------------------------------
// TipButton: IconButton wrapped in a Tooltip.
// The <span> wrapper ensures the tooltip renders even when the button is disabled.
// ---------------------------------------------------------------------------
const TipButton = ({ title, children, ...rest }) => (
  <Tooltip title={title} arrow>
    <span style={{ display: 'inline-flex' }}>
      <IconButton {...rest}>{children}</IconButton>
    </span>
  </Tooltip>
);

const Allergies = props => {
  const { classes, patientId, ptMedicalHistory, getPatientMedicalHistory } = props;

  const initialUserState = { id: 0, name: '', reaction: '' };

  const [values, setValues] = useState(initialUserState);
  const [editvalues, setEditValues] = useState(initialUserState);
  const [allergy, setAllergy] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  // ---------------------------------------------------------------------------
  // Loading states
  // 'save'        — header row save button
  // 'update'      — inline update confirm button
  // `delete-{id}` — delete spinner on that specific row
  // ---------------------------------------------------------------------------
  const [loadingId, setLoadingId] = useState(null);

  // ---------------------------------------------------------------------------
  // Sync Redux → local state
  // Guard removed so an empty array properly clears local state too
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (ptMedicalHistory?.allergies !== undefined)
      setAllergy(ptMedicalHistory.allergies || []);
  }, [ptMedicalHistory]);

  // ---------------------------------------------------------------------------
  // Field handlers
  // ---------------------------------------------------------------------------
  const handleFieldChange = (field, value) =>
    setValues({ ...values, [field]: value });

  const handleEditFieldChange = (field, value) =>
    setEditValues({ ...editvalues, [field]: value });

  // ---------------------------------------------------------------------------
  // Save new allergy
  // ---------------------------------------------------------------------------
  const handleSaveAllergy = async () => {
    if (values.name || values.reaction) {
      setLoadingId('save');
      try {
        const res = await props.saveAllergy(patientId, values);
        if (res.status) {
          setAllergy(res.allergies);
          setValues(initialUserState);
          getPatientMedicalHistory(patientId);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Update existing allergy
  // ---------------------------------------------------------------------------
  const handleUpdateAllergy = async idx => {
    if (editvalues.name || editvalues.reaction) {
      setLoadingId('update');
      try {
        const res = await props.updateAllergy(patientId, editvalues);
        if (res.status) {
          const newState = [...allergy];
          newState[idx] = res.allergy;
          setAllergy(newState);
          setIsEdit(false);
          getPatientMedicalHistory(patientId);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Delete allergy
  // ---------------------------------------------------------------------------
  const handleDeleteAllergy = async id => {
    if (loadingId !== null) return;
    setLoadingId(`delete-${id}`);
    try {
      const res = await props.deleteAllergy(patientId, id);
      if (res.status) {
        setAllergy(res.allergies);
        setValues(initialUserState);
        getPatientMedicalHistory(patientId);
      }
    } finally {
      setLoadingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Edit / clear
  // ---------------------------------------------------------------------------
  const handleEditAllergy = (data, index) => {
    setIsEdit(true);
    if (data)
      setEditValues({
        id: data.id,
        name: data.name,
        reaction: data.reaction,
        editIndex: index,
      });
  };

  const clearAllergy = () => {
    setIsEdit(false);
    setValues(initialUserState);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={classes.templateWrap}>
      <div className={classes.templateBody} style={{ borderTop: 'default' }}>
        <PerfectScrollbar>
          <div style={{ maxHeight: 'calc(100vh - 298px)' }}>
            <Table className={classes.templateTable}>

              {/* ============================================================
                  HEADER ROW — add new allergy
              ============================================================ */}
              <TableHead>
                <TableRow>
                  <TableCell width="25%">
                    <TextField
                      className={classes.textBox}
                      InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                      onChange={e => handleFieldChange('name', e.target.value)}
                      placeholder="Input Name"
                      value={values.name}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      className={classes.textBox}
                      InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                      onChange={e => handleFieldChange('reaction', e.target.value)}
                      placeholder="Input Reaction"
                      value={values.reaction}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                    <TipButton
                      title="Save"
                      disabled={loadingId === 'save'}
                      onClick={handleSaveAllergy}
                      size="small"
                      style={{ color: '#03A484' }}>
                      {loadingId === 'save'
                        ? <CircularProgress size={16} color="inherit" />
                        : <CheckOutlined />}
                    </TipButton>
                    <TipButton
                      title="Clear"
                      disabled={loadingId === 'save'}
                      onClick={clearAllergy}
                      size="small">
                      <ClearOutlined style={{ color: '#DE5555' }} />
                    </TipButton>
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* ============================================================
                  BODY — existing allergies
              ============================================================ */}
              <TableBody>
                {allergy &&
                  allergy.length > 0 &&
                  allergy
                    .sort((a, b) => b.id - a.id)
                    .map((item, idx) => (
                      <TableRow key={item.id}>
                        {isEdit && idx === editvalues.editIndex ? (
                          <>
                            <TableCell width="25%">
                              <TextField
                                className={classes.textBox}
                                InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                                onChange={e => handleEditFieldChange('name', e.target.value)}
                                placeholder="Input Name"
                                value={editvalues.name}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                className={classes.textBox}
                                InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                                onChange={e => handleEditFieldChange('reaction', e.target.value)}
                                placeholder="Input Reaction"
                                value={editvalues.reaction}
                                variant="outlined"
                              />
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>{item.reaction}</TableCell>
                          </>
                        )}
                        <TableCell align="center" width={72}>
                          {isEdit && idx === editvalues.editIndex ? (
                            <>
                              <TipButton
                                title="Update Allergy"
                                disabled={loadingId === 'update'}
                                onClick={() => handleUpdateAllergy(idx)}
                                size="small"
                                style={{ color: '#03A484' }}>
                                {loadingId === 'update'
                                  ? <CircularProgress size={16} color="inherit" />
                                  : <CheckOutlined />}
                              </TipButton>
                              <TipButton
                                title="Cancel"
                                disabled={loadingId === 'update'}
                                onClick={clearAllergy}
                                size="small">
                                <ClearOutlined style={{ color: '#DE5555' }} />
                              </TipButton>
                            </>
                          ) : (
                            <>
                              <TipButton
                                title="Edit"
                                disabled={loadingId !== null}
                                onClick={() => handleEditAllergy(item, idx)}
                                size="small">
                                <Edit color="primary" />
                              </TipButton>
                              <TipButton
                                title="Delete"
                                disabled={loadingId !== null}
                                onClick={() => handleDeleteAllergy(item.id)}
                                size="small">
                                {loadingId === `delete-${item.id}`
                                  ? <CircularProgress size={16} color="inherit" />
                                  : <DeleteOutline style={{ color: '#DE5555' }} />}
                              </TipButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </div>
    </div>
  );
};

Allergies.propTypes = {
  className: PropTypes.string,
};

const mapDispatchToProps = {
  saveAllergy,
  updateAllergy,
  deleteAllergy,
  getPatientMedicalHistory,
};

const mapStateToProps = state => ({
  ptMedicalHistory: state.soap && state.soap.ptMedicalHistory,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Allergies))
);